import type { CadFieldHistoryMissing, CadFieldHistoryResult, CadFieldHistoryValue, CadLandAreaConflict } from "./api";

/**
 * THE FIELD HISTORY PANEL'S PURE HALF (slice data-cad-picker, 2026-10-04, owner spec §2).
 *
 * "Earlier values stay available. Preliminary, certified and supplement values each remain
 * viewable in the field's history." This file turns `GET /api/cad/field-history`'s answer into
 * what `FieldHistoryDisclosure.tsx` and `ProtestValueChoice.tsx` render — and almost nothing else:
 * `field_history.build_history` already composes each value's finished sentence
 * (`ingestion.core.provenance.value_label`, "<Label> <display>: <tag>"), so there is no label to
 * build here. ADR-0002: render `value.label` verbatim; this file only guards the shape and finds
 * the one entry a chooser needs to pre-select.
 *
 * CODED AGAINST THE REAL, LANDED ROUTE — `src/server/field_history.py` and `tests/
 * test_field_history.py`, read directly on 2026-10-04 (an earlier draft of this file was coded
 * against the brief alone, before LVS's server code was on this worktree; that draft's shape —
 * `entries[]` with a `status` field — was wrong and is replaced here).
 */

/** A guard, not a parser: `isHistoryResult` either accepts the real shape or says it does not,
 * rather than letting a malformed 200 reach a screen that assumes `values` exists (the exact
 * defect `ProtestOutcomeTab.isOutcome`'s header documents happening once already). */
export function isHistoryResult(body: unknown): body is Required<Pick<CadFieldHistoryResult, "values" | "not_in_dataset">> & CadFieldHistoryResult {
  const b = body as Partial<CadFieldHistoryResult> | null;
  return !!b && typeof b === "object" && Array.isArray(b.values) && Array.isArray(b.not_in_dataset);
}

/** The server's own order, exactly as sent — newest first. Never re-sorted; a client-side
 * ordering bug must not be able to overrule the one place "newest" is actually decided. Drops
 * only a row that is not a plain object, which cannot be rendered at all. */
export function historyValues(result: CadFieldHistoryResult | null | undefined): CadFieldHistoryValue[] {
  const values = result?.values;
  if (!Array.isArray(values)) return [];
  return values.filter((v): v is CadFieldHistoryValue => !!v && typeof v === "object" && typeof v.label === "string");
}

export function missingStages(result: CadFieldHistoryResult | null | undefined): CadFieldHistoryMissing[] {
  const missing = result?.not_in_dataset;
  if (!Array.isArray(missing)) return [];
  return missing.filter((m): m is CadFieldHistoryMissing => !!m && typeof m === "object" && typeof m.reason === "string");
}

/** The default selection for a chooser: the server's own `default` (= `values[0]`, the current
 * value) when it sent one, else the first value this client itself can see. Never a value this
 * client invents — an empty history has no default, and callers must handle that absence rather
 * than receiving a fabricated one. */
export function defaultValue(result: CadFieldHistoryResult | null | undefined): CadFieldHistoryValue | null {
  // A lot whose lot size and acreage disagree has NO default: neither figure may stand alone.
  if (landAreaConflict(result)) return null;
  const d = result?.default;
  if (d && typeof d === "object" && typeof d.label === "string") return d;
  const [first] = historyValues(result);
  return first ?? null;
}

/** The server's land-area conflict (both figures, each with its source tag, and the note), or null.
 * Guarded like everything else here: a malformed block is treated as absent, never half-rendered. */
export function landAreaConflict(result: CadFieldHistoryResult | null | undefined): CadLandAreaConflict | null {
  const c = result?.land_area_conflict;
  if (!c || typeof c !== "object" || typeof c.note !== "string" || !Array.isArray(c.figures)) return null;
  const figures = c.figures.filter((f) => !!f && typeof f === "object" && typeof f.label === "string");
  return figures.length === 2 ? { ...c, figures } : null;
}

/** Which value is "this one" for a radio list's `key`/selection — a stage+year pair is the
 * wire identity `resolve_value_choice` matches on, not object identity. */
export function sameValue(a: CadFieldHistoryValue | null, b: CadFieldHistoryValue | null): boolean {
  if (!a || !b) return a === b;
  return a.stage === b.stage && a.year === b.year;
}
